<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import SearchBox from '@/components/SearchBox.vue'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import UiRow from '@/components/UiRow.vue'
import { useSearchQuery } from '@/composables/useSearchQuery'
import { SHARING_SEARCH_MIN, searchBy } from '@/settings/grantGroups'

// The entries of one group with a button each (Remove / Unblock), a search from the sixth row on. `above` is a slot for a group of its own
// on top (the blocked users' Add user link).
const props = defineProps<{ items: { id: number; title: string }[]; actionLabel: string; emptyText: string }>()
defineEmits<{ action: [id: number] }>()
const { t } = useI18n()
const query = useSearchQuery()
const searchable = computed(() => props.items.length >= SHARING_SEARCH_MIN)
const shown = computed(() => (searchable.value ? searchBy(props.items, query.value, (i) => [i.title]) : props.items))
</script>

<template>
  <div class="entry-list">
    <slot name="above" />
    <UiGroup v-if="searchable">
      <SearchBox v-model="query" :placeholder="t('list.search')" :clear-label="t('list.clearSearch')" />
      <template #footer>
        <p v-if="query && !shown.length">{{ t('settings.blocked.noMatches') }}</p>
      </template>
    </UiGroup>
    <UiGroup>
      <template #footer>
        <p v-if="!items.length">{{ emptyText }}</p>
      </template>
      <UiList v-if="shown.length">
        <UiRow v-for="item in shown" :key="item.id" class="entry" data-testid="entry-row">
          <span class="title">{{ item.title }}</span>
          <button type="button" class="action" @click="$emit('action', item.id)">{{ actionLabel }}</button>
        </UiRow>
      </UiList>
    </UiGroup>
  </div>
</template>

<style scoped>
.title {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

.action {
  flex: none;
  background: none;
  border: 0;
  color: var(--tg-destructive-text-color);
  font: inherit;
  cursor: pointer;
}
</style>
